import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'

type Warehouse = { id: string; name: string; type: string; active: boolean; createdAt: string }

const typeLabel: Record<string, string> = { storage: 'Armazenagem', picking: 'Fracionamento' }
const typeVariant: Record<string, 'info' | 'warning'> = { storage: 'info', picking: 'warning' }

export function WarehousesPage() {
  const qc = useQueryClient()
  const [createModal, setCreateModal] = useState(false)
  const [editModal, setEditModal] = useState<Warehouse | null>(null)
  const [form, setForm] = useState({ name: '', type: 'storage' })

  const { data: warehouses = [], isLoading } = useQuery<Warehouse[]>({
    queryKey: ['warehouses'],
    queryFn: async () => (await api.get('/warehouses')).data,
  })

  const create = useMutation({
    mutationFn: async () => api.post('/warehouses', form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['warehouses'] }); setCreateModal(false); setForm({ name: '', type: 'storage' }) },
  })

  const update = useMutation({
    mutationFn: async (data: { id: string; name?: string; active?: boolean }) => {
      const { id, ...body } = data
      return api.patch(`/warehouses/${id}`, body)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['warehouses'] }); setEditModal(null) },
  })

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Armazéns</h2>
          <p className="text-sm text-gray-500">Espaços físicos de armazenagem (compartilhados entre clientes)</p>
        </div>
        <Button size="sm" onClick={() => setCreateModal(true)}><Plus className="h-4 w-4" /> Novo armazém</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Nome</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Tipo</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {warehouses.map((w) => (
                  <tr key={w.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-900">{w.name}</td>
                    <td className="px-5 py-3">
                      <Badge variant={typeVariant[w.type] ?? 'default'}>{typeLabel[w.type] ?? w.type}</Badge>
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant={w.active ? 'success' : 'danger'}>{w.active ? 'Ativo' : 'Inativo'}</Badge>
                    </td>
                    <td className="px-5 py-3">
                      <Button variant="ghost" size="sm" onClick={() => setEditModal(w)}>Editar</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Modal title="Novo armazém" open={createModal} onClose={() => setCreateModal(false)} size="sm">
        <div className="space-y-4">
          <Input label="Nome *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Armazenagem Principal" />
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Tipo</label>
            <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
              <option value="storage">Armazenagem (caixas/paletes fechados)</option>
              <option value="picking">Fracionamento / Picking</option>
            </select>
          </div>
          {create.error && <p className="text-sm text-red-600">{(create.error as any)?.response?.data?.title ?? 'Erro'}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setCreateModal(false)}>Cancelar</Button>
            <Button loading={create.isPending} disabled={!form.name} onClick={() => create.mutate()}>Criar</Button>
          </div>
        </div>
      </Modal>

      <Modal title={`Editar — ${editModal?.name}`} open={!!editModal} onClose={() => setEditModal(null)} size="sm">
        {editModal && (
          <div className="space-y-4">
            <Input label="Nome" defaultValue={editModal.name} onChange={e => setEditModal(p => p ? { ...p, name: e.target.value } : p)} />
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="h-4 w-4" checked={editModal.active}
                onChange={e => setEditModal(p => p ? { ...p, active: e.target.checked } : p)} />
              <span className="text-sm text-gray-700">Ativo</span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setEditModal(null)}>Cancelar</Button>
              <Button loading={update.isPending} onClick={() => update.mutate({ id: editModal.id, name: editModal.name, active: editModal.active })}>Salvar</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
